import { Plus, Shield, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { ChipSelect, EmptyState, Field, Loading } from '../components';
import { Link, useRouter } from '../router';
import { useApp } from '../store';
import type { GuildSummary } from '../types';

export function CommunityTabs({ active }: { active: 'players' | 'guilds' }) {
  return (
    <div className="tab-row" role="tablist" aria-label="Community">
      <Link to="/players" role="tab" aria-selected={active === 'players'} className={`tab ${active === 'players' ? 'active' : ''}`}>Players</Link>
      <Link to="/guilds" role="tab" aria-selected={active === 'guilds'} className={`tab ${active === 'guilds' ? 'active' : ''}`}>Guilds</Link>
    </div>
  );
}

function CreateGuildForm({ onCreated }: { onCreated: (slug: string) => void }) {
  const { toast } = useApp();
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [description, setDescription] = useState('');
  const [joinPolicy, setJoinPolicy] = useState('open');
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await api.createGuild({ name, tag, description, joinPolicy });
      toast('Guild created. Rally your people.', 'success');
      onCreated(result.slug);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not create the guild.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="stack-form edit-form" onSubmit={submit}>
      <div className="field-row">
        <Field label="Guild name">
          <input value={name} onChange={(event) => setName(event.target.value)} minLength={3} maxLength={50} required />
        </Field>
        <Field label="Tag (optional)" hint="Up to 5 characters, shows next to member names.">
          <input value={tag} onChange={(event) => setTag(event.target.value.toUpperCase())} maxLength={5} placeholder="TCFT" />
        </Field>
      </div>
      <Field label="What is this guild about?">
        <textarea value={description} onChange={(event) => setDescription(event.target.value)} maxLength={500} rows={2} />
      </Field>
      <Field label="Joining">
        <ChipSelect
          options={[
            { value: 'open', label: 'Open — anyone can join' },
            { value: 'apply', label: 'Apply — officers approve' },
            { value: 'invite', label: 'Invite only' }
          ]}
          selected={[joinPolicy]}
          onToggle={setJoinPolicy}
          single
        />
      </Field>
      <button className="primary small" type="submit" disabled={busy}>{busy ? 'Creating…' : 'Create guild'}</button>
    </form>
  );
}

export function Guilds() {
  const { authStatus } = useApp();
  const { navigate } = useRouter();
  const [guilds, setGuilds] = useState<GuildSummary[] | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.guilds()
      .then((data) => setGuilds(data.guilds))
      .catch(() => setGuilds([]));
  }, []);

  const mine = guilds?.filter((guild) => guild.myRole) ?? [];
  const rest = guilds?.filter((guild) => !guild.myRole) ?? [];

  return (
    <div className="page">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Community</span>
          <h1>Guilds</h1>
        </div>
        {authStatus === 'authed' && (
          <button className="primary small" onClick={() => setCreating((current) => !current)}>
            <Plus size={15} /> {creating ? 'Close' : 'Create guild'}
          </button>
        )}
      </div>

      <CommunityTabs active="guilds" />

      {creating && <CreateGuildForm onCreated={(slug) => navigate(`/guilds/${slug}?created=1`)} />}

      {guilds === null ? (
        <Loading label="Loading guilds…" />
      ) : guilds.length === 0 ? (
        <EmptyState
          title="No guilds yet"
          hint="Guilds get their own page, event calendar, and announcements. Start the first one."
          action={authStatus === 'authed' ? <button className="primary small" onClick={() => setCreating(true)}>Create guild</button> : <Link to="/signup" className="primary small">Create account</Link>}
        />
      ) : (
        <>
          {mine.length > 0 && (
            <>
              <h2>Your guilds</h2>
              <div className="guild-grid">{mine.map((guild) => <GuildCard key={guild.slug} guild={guild} />)}</div>
            </>
          )}
          {rest.length > 0 && (
            <>
              {mine.length > 0 && <h2>Discover</h2>}
              <div className="guild-grid">{rest.map((guild) => <GuildCard key={guild.slug} guild={guild} />)}</div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function GuildCard({ guild }: { guild: GuildSummary }) {
  return (
    <Link to={`/guilds/${guild.slug}`} className="guild-card">
      <div className="guild-card-head">
        <span className="guild-tag-badge"><Shield size={13} /> {guild.tag ?? guild.name.slice(0, 4).toUpperCase()}</span>
        {guild.myRole && <span className="pill mint">{guild.myRole}</span>}
      </div>
      <h3>{guild.name}</h3>
      {guild.description && <p>{guild.description}</p>}
      <div className="session-meta">
        <span><Users size={13} /> {guild.memberCount} member{guild.memberCount === 1 ? '' : 's'}</span>
        <span>{guild.upcomingCount} upcoming</span>
        <span className="pill">{guild.joinPolicy === 'open' ? 'Open' : guild.joinPolicy === 'apply' ? 'Apply to join' : 'Invite only'}</span>
      </div>
    </Link>
  );
}
